<template>
    <div>
        <Panel
            :title="$t('Identity Providers')"
            :delete-button="selected.length > 0"
            :add-button="canEdit ? $t('Add Identity Provider') : ''"
            :total-rows="totalRows"
            :page="page"
            @input="openProvider(-1)"
            @delete="handleDelete"
            @change="changePage"
        >
            <table id="identityproviders_table" class="cic-table">
                <thead>
                    <tr>
                        <th></th>
                        <th>{{ $t('Name') }}</th>
                        <th>{{ $t('Metadata URL') }}</th>
                        <th v-if="canEdit"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="provider in providers">
                        <td>
                            <span
                                :title="$t('User SAML Attribute Values')"
                                class="user-icon"
                                @click="openMappings(provider.providerId)"
                            >
                            </span>
                        </td>
                        <td class="primary" @click="openProvider(provider.providerId)">
                            {{ provider.name }}
                        </td>
                        <td>
                            <a :href="ssoHost + 'metadata/' + provider.guid" target="_blank">
                                {{ ssoHost + 'metadata/' + provider.guid }}
                            </a>
                        </td>
                        <td v-if="canEdit" class="td-checkbox">
                            <cic-checkbox
                                v-if="canEditDetails(provider.orgId)"
                                :value="selected.includes(provider.providerId)"
                                @click="handleSelected(provider.providerId)"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </Panel>

        <edit-identity-provider
            v-if="editing && !$store.showLoadingOverlay"
            :item="item"
            :enabled="canEditDetails(item.orgId)"
            @blur="closePopup"
            @input="saveItem"
            @updateFieldValue="updateFieldValue"
        />

        <edit-identity-provider-users
            v-if="editingMapping"
            :item="item"
            :users="users"
            :enabled="canMap"
            @blur="closePopup"
            @clear="updateMapping({}, false)"
            @import="importMapping"
            @input="saveMapping"
            @updateFieldValue="updateFieldValue"
        />
        <div v-if="$store.showLoadingOverlay" class="cic-overlay"></div>
    </div>
</template>

<script>
    import Panel from '@/components/container/Panel';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import TableUtils from '@/mixins/TableUtils';
    import { deleteItems, load, save } from '@/utils/Http';
    import EditIdentityProvider from './EditIdentityProvider';
    import EditIdentityProviderUsers from './EditIdentityProviderUsers';

    export default {
        name: 'IdentityProviders',
        components: {
            Panel,
            CicCheckbox,
            EditIdentityProviderUsers,
            EditIdentityProvider,
        },
        mixins: [TableUtils],
        data() {
            return {
                selected: [],
                providers: [],
                page: 1,
                ssoHost: '',
                totalRows: 0,
                editing: false,
                editingMapping: false,
                item: null,
                users: [],
            };
        },
        computed: {
            canEdit() {
                return this.$store.hasRight('EDIT_IDENTITY_PROVIDERS');
            },

            canMap() {
                return this.$store.hasRight('EDIT_IDENTITY_PROVIDER_USER_MAPPING');
            },
        },
        created() {
            this.loadTable();
        },
        methods: {
            // Check if element is editable
            canEditDetails(orgId) {
                return this.canEdit && orgId === this.$store.user.orgId;
            },

            closePopup() {
                this.item = null;
                this.editing = false;
                this.editingMapping = false;
            },

            handleDelete() {
                deleteItems('sso/delete', { providerIds: this.selected }, () => {
                    this.selected = [];
                    this.loadTable();
                });
            },

            importMapping(users) {
                this.updateMapping({ mappings: JSON.stringify(users), partial: 1 }, false);
            },

            // Load table of search results
            loadTable() {
                this.closePopup();
                load('sso/get', { page: this.page }, (response) => {
                    this.providers = response.providers;
                    this.ssoHost = response.ssoHost.replace('{server.name}', window.location.host);
                    this.totalRows = response.totalRows;
                });
            },

            openMappings(providerId) {
                load('sso/getmappings', { providerId: providerId }, (response) => {
                    this.users = response.users;
                    this.item = {
                        providerId: providerId,
                        name: this.providers.find((x) => x.providerId === providerId).name,
                        users: response.mappings,
                    };
                    this.editingMapping = true;
                });
            },

            openProvider(providerId) {
                if (providerId > 0) {
                    load('sso/edit', { providerId: providerId }, (response) => {
                        this.item = response.data;
                        if (this.item.authContext === null) {
                            this.item.authContext = '';
                        }
                        this.editing = true;
                    });
                } else {
                    this.item = {
                        providerId: null,
                        orgId: this.$store.user.orgId,
                        acsBinding: 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST',
                        samlName: 'NameID',
                        nameIdFormat: 'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified',
                        ssoBinding: 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect',
                        ssoBindingMethod: 1,
                        allowForSubOrgs: false,
                        allowInitiateSso: false,
                        authContext: '',
                    };
                    this.editing = true;
                }
            },

            saveItem() {
                save('sso/update', { data: JSON.stringify(this.item) }, () => {
                    this.selected = [];
                    if (!this.item.providerId) {
                        this.page = 1;
                    }
                    this.loadTable();
                });
            },

            saveMapping() {
                this.updateMapping({
                    mappings: JSON.stringify(
                        this.item.users.map((x) => ({ userId: x.userId, samlValue: x.samlValue })),
                    ),
                });
            },

            updateMapping(params, close = true) {
                params['providerId'] = this.item.providerId;
                save('sso/updatemappings', params, () => {
                    if (close) {
                        this.closePopup();
                    } else {
                        this.openMappings(this.item.providerId);
                    }
                });
            },
        },
    };
</script>
